import { ChangeDetectionStrategy, Component, computed, inject, input, signal, ViewEncapsulation } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { adaptEchartsOptions, BaseEchartsDirective } from '@examdojo/core/widget';
import { EChartsOption, SeriesOption } from 'echarts';
import { LineChart } from 'echarts/charts';
import { GridComponent, MarkLineComponent } from 'echarts/components';
import * as echarts from 'echarts/core';
import katex from 'katex';
import { NgxEchartsDirective, provideEchartsCore } from 'ngx-echarts';

import { CHART_COLORS, CHART_GRID_COMPACT, CHART_X_AXIS_STYLE, CHART_Y_AXIS_STYLE } from '../../../shared/chart-shared';
import { generatePdfData, normalPdf } from '../../../shared/normal-dist-math.util';
import {
  NormalCurveConfig,
  NormalDistributionChartParams,
  ShadedRegion,
  TopLabelConfig,
} from './normal-distribution-chart.model';
import { NormalChartPreset, resolvePreset } from './normal-distribution-chart.presets';

echarts.use([LineChart, GridComponent, MarkLineComponent]);

const DEFAULT_COLOR = '#6366f1';
const DEFAULT_LINE_WIDTH = 2.5;
const DEFAULT_NUM_POINTS = 200;

const EMPIRICAL_BAND_COLORS = {
  band1Sigma: 'rgba(239, 68, 68, 0.3)',
  band2Sigma: 'rgba(20, 184, 166, 0.3)',
  band3Sigma: 'rgba(14, 165, 233, 0.3)',
  tails: 'rgba(14, 165, 233, 0.15)',
};

/** Use neutral-500 so adaptEchartsOptions remaps it for dark mode. */
const BRACKET_COLOR = '#6b7280';

@Component({
  selector: 'dojo-normal-distribution-chart',
  standalone: true,
  imports: [NgxEchartsDirective],
  template: `
    <div class="normal-distribution-chart-wrapper">
      <div
        echarts
        class="normal-distribution-chart"
        [options]="adaptedOptions()"
        [initOpts]="initOpts"
        [autoResize]="true"
        (chartInit)="onChartInit($event)"
      ></div>
      @if (titleHtml()) {
        <div class="chart-title" [innerHTML]="titleHtml()"></div>
      }
      @for (a of positionedAnnotations(); track $index) {
        <span
          class="chart-annotation"
          [attr.data-align]="a.align"
          [style.left.px]="a.left"
          [style.top.px]="a.top"
          [innerHTML]="a.html"
        ></span>
      }
    </div>
  `,
  styles: `
    dojo-normal-distribution-chart {
      display: flex;
      flex: 1 1 0;
      width: 100%;
      min-height: 0;

      .normal-distribution-chart-wrapper {
        position: relative;
        width: 100%;
        height: 100%;
        overflow: visible;
      }

      .chart-title {
        position: absolute;
        top: 50%;
        left: calc(50% + 10px);
        transform: translate(-50%, -50%);
        font-size: 13px;
        pointer-events: none;
        white-space: nowrap;
        z-index: 1;
      }

      .normal-distribution-chart {
        width: 100%;
        height: 100%;
      }

      .chart-annotation {
        position: absolute;
        pointer-events: none;
        white-space: nowrap;
        font-size: 13px;

        &[data-align='center'] {
          transform: translate(-50%, -50%);
        }
        &[data-align='left'] {
          transform: translate(4px, -50%);
        }
        &[data-align='right'] {
          transform: translate(calc(-100% - 4px), -50%);
        }
      }
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  providers: [provideEchartsCore({ echarts })],
})
export class NormalDistributionChartComponent extends BaseEchartsDirective {
  private readonly sanitizer = inject(DomSanitizer);

  readonly params = input.required<NormalDistributionChartParams | NormalChartPreset>();
  readonly positionedAnnotations = signal<
    Array<{ left: number; top: number; html: SafeHtml; align: 'left' | 'center' | 'right' }>
  >([]);

  private readonly resolvedParams = computed<NormalDistributionChartParams>(() => {
    const p = this.params();
    return 'preset' in p ? resolvePreset(p) : p;
  });

  readonly titleHtml = computed<SafeHtml | null>(() => {
    const title = this.resolvedParams().title;
    if (!title) {
      return null;
    }
    const html = katex.renderToString(title.text, { throwOnError: false, output: 'html' });
    const colorStyle = title.color ? `color: ${title.color}` : '';
    return this.sanitizer.bypassSecurityTrustHtml(`<span style="${colorStyle}">${html}</span>`);
  });

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private chartInstance: any = null;

  readonly adaptedOptions = computed(() => adaptEchartsOptions(this.buildOptions(), this.document, this.isDark()));

  onChartInit(chart: unknown): void {
    this.chartInstance = chart;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (chart as any).on('finished', () => this.updateAnnotationPositions());
  }

  private buildOptions(): EChartsOption {
    const p = this.resolvedParams();
    if (p.showEmpiricalRule) {
      return this.buildEmpiricalRuleOptions(p);
    }
    return this.buildStandardOptions(p);
  }

  private buildStandardOptions(p: NormalDistributionChartParams): EChartsOption {
    const numPoints = p.numPoints ?? DEFAULT_NUM_POINTS;
    const shadedSeries: SeriesOption[] = (p.shadedRegions ?? []).map((region) =>
      this.buildShadedRegionSeries(p.curves[0], region, numPoints),
    );

    if (p.shadeComplement && p.shadedRegions?.length) {
      const complementRegions = this.buildComplementRegions(p.shadedRegions, p.xMin, p.xMax);
      const complementColor = 'rgba(14, 165, 233, 0.25)'; // sky-500

      for (const region of complementRegions) {
        shadedSeries.push(this.buildShadedRegionSeries(p.curves[0], { ...region, color: complementColor }, numPoints));
      }
    }

    const curveSeries: SeriesOption[] = p.curves.map((curve) =>
      this.buildCurveSeries(curve, p.xMin, p.xMax, numPoints),
    );

    if (p.topLabels?.length) {
      const curve = p.curves[0];
      const defaultColor = curve.color ?? DEFAULT_COLOR;
      const normalized: TopLabelConfig[] = p.topLabels.map((tl) =>
        typeof tl === 'number' ? { value: tl, text: String(tl) } : tl,
      );

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const markLineEntries: any[] = [];
      for (const { value, color, lineType, lineWidth, extent } of normalized) {
        const lineColor = color ?? defaultColor;
        const type: 'solid' | 'dashed' = lineType ?? 'dashed';
        const width = lineWidth ?? 1;

        if (extent === 'full') {
          markLineEntries.push({
            xAxis: value,
            lineStyle: { color: lineColor, width, type },
            label: { show: false },
          });
        } else {
          markLineEntries.push([
            { coord: [value, 0], lineStyle: { color: lineColor, width, type } },
            { coord: [value, normalPdf(value, curve.mu, curve.sigma)] },
          ]);
        }
      }

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (curveSeries[0] as any).markLine = {
        silent: true,
        symbol: ['none', 'none'],
        animation: false,
        label: { show: false },
        data: markLineEntries,
      };
    }

    const series: SeriesOption[] = [...shadedSeries, ...curveSeries];

    return {
      animation: false,
      grid: { ...CHART_GRID_COMPACT, right: p.gridRight ?? 20 },
      xAxis: {
        type: 'value',
        min: p.xMin,
        max: p.xMax,
        ...CHART_X_AXIS_STYLE,
        splitLine: { show: false },
        ...(p.showAxisLabels === false ? { axisLabel: { show: false } } : {}),
        ...(p.xAxisLabel
          ? {
              name: p.xAxisLabel,
              nameLocation: 'middle',
              nameGap: 28,
              nameTextStyle: { fontSize: 11, color: CHART_COLORS.label },
            }
          : {}),
      },
      yAxis: p.showYAxis
        ? {
            type: 'value',
            ...CHART_Y_AXIS_STYLE,
            splitLine: { show: false },
            axisLine: { show: true, lineStyle: { color: CHART_COLORS.axisLine } },
            ...(p.yAxisLabel
              ? {
                  name: p.yAxisLabel,
                  nameLocation: 'middle' as const,
                  nameGap: 36,
                  nameTextStyle: { fontSize: 11, color: CHART_COLORS.label },
                }
              : {}),
          }
        : { type: 'value', show: false },
      series,
      tooltip: { show: false },
    };
  }

  private buildEmpiricalRuleOptions(p: NormalDistributionChartParams): EChartsOption {
    const { mu, sigma } = p.curves[0];
    const xMin = mu - 4 * sigma;
    const xMax = mu + 4 * sigma;
    const peak = normalPdf(mu, mu, sigma);
    const pts = 30;

    // Colored band series
    const bands = [
      { from: xMin, to: mu - 3 * sigma, color: EMPIRICAL_BAND_COLORS.tails, pts },
      { from: mu - 3 * sigma, to: mu - 2 * sigma, color: EMPIRICAL_BAND_COLORS.band3Sigma, pts },
      { from: mu - 2 * sigma, to: mu - sigma, color: EMPIRICAL_BAND_COLORS.band2Sigma, pts },
      { from: mu - sigma, to: mu + sigma, color: EMPIRICAL_BAND_COLORS.band1Sigma, pts: pts * 2 },
      { from: mu + sigma, to: mu + 2 * sigma, color: EMPIRICAL_BAND_COLORS.band2Sigma, pts },
      { from: mu + 2 * sigma, to: mu + 3 * sigma, color: EMPIRICAL_BAND_COLORS.band3Sigma, pts },
      { from: mu + 3 * sigma, to: xMax, color: EMPIRICAL_BAND_COLORS.tails, pts },
    ];

    const bandSeries: SeriesOption[] = bands.map((b) => ({
      type: 'line' as const,
      smooth: false,
      symbol: 'none',
      data: generatePdfData(mu, sigma, b.from, b.to, b.pts),
      lineStyle: { width: 0 },
      areaStyle: { color: b.color },
      itemStyle: { color: b.color },
      silent: true,
    }));

    // Bracket y-positions as fractions of peak
    const y68 = 0.68 * peak;
    const y95 = 0.42 * peak;
    const y997 = 0.19 * peak;
    const capH = 0.035 * peak;

    const solidStyle = { color: BRACKET_COLOR, width: 1.2, type: 'solid' as const };
    const dashedStyle = { color: '#d1d5db', width: 1, type: 'dashed' as const };
    const bracketLabel = (text: string) => ({
      show: true,
      formatter: () => text,
      position: 'middle' as const,
      fontSize: 13,
      fontWeight: 600,
      color: BRACKET_COLOR,
    });

    // Outline curve + brackets + vertical lines + labels
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const outlineSeries: any = {
      type: 'line',
      smooth: false,
      symbol: 'none',
      data: generatePdfData(mu, sigma, xMin, xMax, 200),
      lineStyle: { width: 2, color: BRACKET_COLOR },
      itemStyle: { color: BRACKET_COLOR },
      z: 10,
      markLine: {
        silent: true,
        symbol: 'none',
        lineStyle: solidStyle,
        label: { show: false },
        data: [
          // Vertical dashed center line at μ
          [{ coord: [mu, 0], lineStyle: dashedStyle }, { coord: [mu, peak] }],
          // Vertical dashed lines extending up to their bracket height
          [{ coord: [mu - sigma, 0], lineStyle: dashedStyle }, { coord: [mu - sigma, y68] }],
          [{ coord: [mu + sigma, 0], lineStyle: dashedStyle }, { coord: [mu + sigma, y68] }],
          [{ coord: [mu - 2 * sigma, 0], lineStyle: dashedStyle }, { coord: [mu - 2 * sigma, y95] }],
          [{ coord: [mu + 2 * sigma, 0], lineStyle: dashedStyle }, { coord: [mu + 2 * sigma, y95] }],
          [{ coord: [mu - 3 * sigma, 0], lineStyle: dashedStyle }, { coord: [mu - 3 * sigma, y997] }],
          [{ coord: [mu + 3 * sigma, 0], lineStyle: dashedStyle }, { coord: [mu + 3 * sigma, y997] }],
          // 68% bracket + caps + label
          [{ coord: [mu - sigma, y68] }, { coord: [mu + sigma, y68], label: bracketLabel('68%') }],
          [{ coord: [mu - sigma, y68 - capH] }, { coord: [mu - sigma, y68 + capH] }],
          [{ coord: [mu + sigma, y68 - capH] }, { coord: [mu + sigma, y68 + capH] }],
          // 95% bracket + caps + label
          [{ coord: [mu - 2 * sigma, y95] }, { coord: [mu + 2 * sigma, y95], label: bracketLabel('95%') }],
          [{ coord: [mu - 2 * sigma, y95 - capH] }, { coord: [mu - 2 * sigma, y95 + capH] }],
          [{ coord: [mu + 2 * sigma, y95 - capH] }, { coord: [mu + 2 * sigma, y95 + capH] }],
          // 99.7% bracket + caps + label
          [{ coord: [mu - 3 * sigma, y997] }, { coord: [mu + 3 * sigma, y997], label: bracketLabel('99.7%') }],
          [{ coord: [mu - 3 * sigma, y997 - capH] }, { coord: [mu - 3 * sigma, y997 + capH] }],
          [{ coord: [mu + 3 * sigma, y997 - capH] }, { coord: [mu + 3 * sigma, y997 + capH] }],
        ],
      },
    };

    return {
      animation: false,
      grid: { top: 16, bottom: 8, left: 12, right: 12, containLabel: true },
      xAxis: {
        type: 'value',
        min: xMin,
        max: xMax,
        interval: sigma,
        ...CHART_X_AXIS_STYLE,
        splitLine: { show: false },
        axisTick: { show: false },
        axisLabel: {
          fontSize: 11,
          color: CHART_COLORS.label,
          formatter: (value: number) => {
            const n = Math.round((value - mu) / sigma);
            if (Math.abs(n) > 3) {
              return '';
            }
            if (n === 0) {
              return 'μ';
            }
            const sign = n > 0 ? '+' : '−';
            const absN = Math.abs(n);
            return absN === 1 ? `μ ${sign} σ` : `μ ${sign} ${absN}σ`;
          },
        },
      },
      yAxis: { type: 'value', show: false, min: 0 },
      series: [...bandSeries, outlineSeries],
      tooltip: { show: false },
    };
  }

  private buildCurveSeries(curve: NormalCurveConfig, xMin: number, xMax: number, numPoints: number): SeriesOption {
    const color = curve.color ?? DEFAULT_COLOR;
    const lineWidth = curve.lineWidth ?? DEFAULT_LINE_WIDTH;
    const data = generatePdfData(curve.mu, curve.sigma, xMin, xMax, numPoints);

    const series: SeriesOption = {
      type: 'line',
      smooth: false,
      symbol: 'none',
      data,
      lineStyle: {
        width: lineWidth,
        color,
        type: curve.lineStyle === 'dashed' ? 'dashed' : 'solid',
      },
      itemStyle: { color },
    };

    if (curve.areaFill) {
      const r = parseInt(color.slice(1, 3), 16);
      const g = parseInt(color.slice(3, 5), 16);
      const b = parseInt(color.slice(5, 7), 16);
      (series as Record<string, unknown>)['areaStyle'] = {
        color: {
          type: 'linear',
          x: 0,
          y: 0,
          x2: 0,
          y2: 1,
          colorStops: [
            { offset: 0, color: `rgba(${r}, ${g}, ${b}, 0.4)` },
            { offset: 1, color: `rgba(${r}, ${g}, ${b}, 0.05)` },
          ],
        },
      };
    }

    return series;
  }

  private buildShadedRegionSeries(curve: NormalCurveConfig, region: ShadedRegion, numPoints: number): SeriesOption {
    const color = curve.color ?? DEFAULT_COLOR;
    const fillColor =
      region.color ??
      (() => {
        const r = parseInt(color.slice(1, 3), 16);
        const g = parseInt(color.slice(3, 5), 16);
        const b = parseInt(color.slice(5, 7), 16);
        return `rgba(${r}, ${g}, ${b}, 0.3)`;
      })();

    return {
      type: 'line',
      smooth: false,
      symbol: 'none',
      data: generatePdfData(curve.mu, curve.sigma, region.from, region.to, Math.max(40, Math.round(numPoints / 3))),
      lineStyle: { width: 0 },
      areaStyle: { color: fillColor },
      itemStyle: { color: fillColor },
      silent: true,
    };
  }

  private updateAnnotationPositions(): void {
    const p = this.resolvedParams();
    const hasAnnotations = !!p.annotations?.length;
    const hasTopLabels = !!p.topLabels?.length;

    if (!this.chartInstance || (!hasAnnotations && !hasTopLabels)) {
      this.positionedAnnotations.set([]);
      return;
    }

    try {
      const defaultColor = p.curves[0].color ?? DEFAULT_COLOR;
      const gridRect = this.getGridRect();
      const positions: Array<{ left: number; top: number; html: SafeHtml; align: 'left' | 'center' | 'right' }> = [];

      if (hasAnnotations) {
        for (const a of p.annotations!) {
          const pixel = this.chartInstance.convertToPixel('grid', [a.x, a.y]);
          const pixelX = (pixel[0] as number) + (a.dx ?? 0);
          const color = a.color ?? defaultColor;
          const html = katex.renderToString(a.text, { throwOnError: false, output: 'html' });
          positions.push({
            left: pixelX,
            top: pixel[1] as number,
            html: this.sanitizer.bypassSecurityTrustHtml(`<span style="color: ${color}">${html}</span>`),
            align: a.align ?? this.determineAlign(pixelX, gridRect, 'inward'),
          });
        }
      }

      if (hasTopLabels) {
        const curve = p.curves[0];
        const curveColor = curve.color ?? DEFAULT_COLOR;
        const normalized: TopLabelConfig[] = p.topLabels!.map((tl) =>
          typeof tl === 'number' ? { value: tl, text: String(tl) } : tl,
        );

        for (const { value, text, color: labelColor, extent } of normalized) {
          const color = labelColor ?? curveColor;
          const pixel = this.chartInstance.convertToPixel('grid', [value, 0]);
          const pixelX = pixel[0] as number;
          let topPx: number;
          // Min top position: leave room for the label text (~8px half-height above this point)
          const minTop = gridRect ? (gridRect.y as number) - 4 : 8;
          if (extent === 'full') {
            topPx = minTop;
          } else {
            const curvePixel = this.chartInstance.convertToPixel('grid', [
              value,
              normalPdf(value, curve.mu, curve.sigma),
            ]);
            topPx = Math.max(minTop, (curvePixel[1] as number) - 14);
          }
          const isPlain = /^[a-zA-Z0-9.+\-=\s]+$/.test(text);
          const katexInput = isPlain ? `\\textbf{${text}}` : `\\mathbf{${text}}`;
          const html = katex.renderToString(katexInput, { throwOnError: false, output: 'html' });
          positions.push({
            left: pixelX,
            top: topPx,
            html: this.sanitizer.bypassSecurityTrustHtml(
              `<span style="color: ${color}; font-size: 12px">${html}</span>`,
            ),
            align: this.determineAlign(pixelX, gridRect, 'outward'),
          });
        }
      }

      this.positionedAnnotations.set(positions);
    } catch {
      // Chart may not be ready yet
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private getGridRect(): { x: number; y: number; width: number; height: number } | null {
    try {
      return this.chartInstance.getModel().getComponent('grid').coordinateSystem.getRect();
    } catch {
      return null;
    }
  }

  /**
   * @param direction 'outward' = extend away from center (for boundary top labels),
   *                  'inward' = extend toward center (for region annotations)
   */
  private determineAlign(
    pixelX: number,
    gridRect: { x: number; width: number } | null,
    direction: 'inward' | 'outward',
  ): 'left' | 'center' | 'right' {
    if (!gridRect) {
      return 'center';
    }
    const rel = (pixelX - gridRect.x) / gridRect.width;
    if (direction === 'outward') {
      if (rel > 0.6) {
        return 'left';
      } // right side → text extends rightward (away from center)
      if (rel < 0.4) {
        return 'right';
      } // left side → text extends leftward (away from center)
    } else {
      if (rel > 0.6) {
        return 'right';
      } // right side → text extends leftward (toward center)
      if (rel < 0.4) {
        return 'left';
      } // left side → text extends rightward (toward center)
    }
    return 'center';
  }

  private buildComplementRegions(
    regions: ShadedRegion[],
    xMin: number,
    xMax: number,
  ): Array<{ from: number; to: number }> {
    const sorted = [...regions].sort((a, b) => a.from - b.from);
    const complement: Array<{ from: number; to: number }> = [];
    let prevEnd = xMin;

    for (const region of sorted) {
      if (region.from > prevEnd) {
        complement.push({ from: prevEnd, to: region.from });
      }
      prevEnd = Math.max(prevEnd, region.to);
    }

    if (prevEnd < xMax) {
      complement.push({ from: prevEnd, to: xMax });
    }

    return complement;
  }
}
